/**
 * QZ Tray Installation Wizard Dialog
 * 
 * Shows step-by-step instructions for installing QZ Tray
 * when user enables "Additional Auto Printing Mode"
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stepper, Step, StepLabel, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { isQzTrayAvailable, getQzPrinters } from './qzTrayService';

const QZ_TRAY_DOWNLOAD_URL = 'https://github.com/qzind/tray/releases/latest';
// Local installer path - stored in public/downloads folder
const QZ_TRAY_LOCAL_INSTALLER = '/downloads/qz-tray-2.2.5-x86_64.exe';

const steps = ['Download QZ Tray', 'Install & Run', 'Verify Connection'];

const QzTrayInstallDialog = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [error, setError] = useState(null);

  // Check connection when dialog opens or when on verify step
  useEffect(() => {
    if (open && activeStep === 2) {
      checkConnection();
    }
  }, [open, activeStep]);

  const checkConnection = async () => {
    setIsChecking(true);
    setError(null);
    try {
      const available = await isQzTrayAvailable();
      setIsConnected(available);
      if (available) {
        const printerList = await getQzPrinters();
        setPrinters(printerList);
      }
    } catch (err) {
      setError(err.message);
      setIsConnected(false);
    } finally {
      setIsChecking(false);
    }
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      // Finish
      if (isConnected) {
        onSuccess(printers);
        onClose();
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleDownload = () => {
    // Download from local assets
    const link = document.createElement('a');
    link.href = QZ_TRAY_LOCAL_INSTALLER;
    link.download = 'qz-tray-2.2.5-x86_64.exe';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenDownloadPage = () => {
    window.open(QZ_TRAY_DOWNLOAD_URL, '_blank');
  };

  const handleClose = () => {
    setActiveStep(0);
    setIsConnected(false);
    setPrinters([]);
    setError(null);
    onClose();
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <DownloadIcon sx={{ fontSize: 60, color: '#1976d2', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 16px 0' }}>Download QZ Tray</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              QZ Tray is a free application that enables silent printing from your browser.
              Click the button below to download the installer.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                sx={{ textTransform: 'none' }}
              >
                Download QZ Tray (Bundled)
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenDownloadPage}
                sx={{ textTransform: 'none' }}
              >
                Download from GitHub
              </Button>
            </div>
            <p style={{ color: '#999', fontSize: '12px', marginTop: '16px' }}>
              File size: ~100 MB | Windows x64 | Version 2.2.5
            </p>
          </div>
        );

      case 1:
        return (
          <div style={{ padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>Install & Run QZ Tray</h3>
            <ol style={{ color: '#666', lineHeight: '2', paddingLeft: '20px' }}>
              <li>
                <strong>Run the installer</strong> (qz-tray-2.x.x.exe)
              </li>
              <li>
                <strong>Follow the installation wizard</strong>
                <ul style={{ marginTop: '5px', color: '#888' }}>
                  <li>Accept the license agreement</li>
                  <li>Choose installation location (default is fine)</li>
                  <li>Click "Install"</li>
                </ul>
              </li>
              <li>
                <strong>Start QZ Tray</strong>
                <ul style={{ marginTop: '5px', color: '#888' }}>
                  <li>It should start automatically after installation</li>
                  <li>Look for the QZ icon in your system tray (bottom-right)</li>
                </ul>
              </li>
              <li>
                <strong>Allow browser connection</strong>
                <ul style={{ marginTop: '5px', color: '#888' }}>
                  <li>When prompted, click "Allow" to let the browser connect</li>
                </ul>
              </li>
            </ol>
            <div style={{ 
              backgroundColor: '#fff3e0', 
              padding: '12px', 
              borderRadius: '4px', 
              marginTop: '16px',
              border: '1px solid #ffcc80'
            }}>
              <strong>💡 Tip:</strong> QZ Tray runs in the background. You'll see its icon in the system tray.
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {isChecking ? (
              <>
                <CircularProgress size={60} sx={{ marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0' }}>Checking Connection...</h3>
                <p style={{ color: '#666' }}>Looking for QZ Tray...</p>
              </>
            ) : isConnected ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: '#4caf50' }}>Connected Successfully!</h3>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  QZ Tray is running and ready for silent printing.
                </p>
                {printers.length > 0 && (
                  <div style={{ 
                    backgroundColor: '#e8f5e9', 
                    padding: '12px', 
                    borderRadius: '4px',
                    textAlign: 'left'
                  }}>
                    <strong>🖨️ Available Printers ({printers.length}):</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {printers.slice(0, 5).map((printer, i) => (
                        <li key={i} style={{ color: '#666' }}>{printer}</li>
                      ))}
                      {printers.length > 5 && (
                        <li style={{ color: '#999' }}>...and {printers.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <ErrorIcon sx={{ fontSize: 60, color: '#f44336', marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: '#f44336' }}>Connection Failed</h3>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  Could not connect to QZ Tray. Please make sure:
                </p>
                <ul style={{ textAlign: 'left', color: '#666', display: 'inline-block' }}>
                  <li>QZ Tray is installed</li>
                  <li>QZ Tray is running (check system tray)</li>
                  <li>You allowed browser connection when prompted</li>
                </ul>
                {error && (
                  <p style={{ color: '#f44336', fontSize: '12px', marginTop: '12px' }}>
                    Error: {error}
                  </p>
                )}
                <div style={{ marginTop: '16px' }}>
                  <Button
                    variant="outlined"
                    onClick={checkConnection}
                    startIcon={<PrintIcon />}
                  >
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', paddingBottom: 0 }}>
        <PrintIcon sx={{ fontSize: 32, color: '#1976d2', marginRight: '8px', verticalAlign: 'middle' }} />
        Setup Silent Printing
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ padding: '20px 0' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <div style={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={activeStep === 2 && !isConnected}
        >
          {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QzTrayInstallDialog;
